import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Book,
  Search,
  Plus,
  Edit,
  Trash2,
  X,
  CheckCircle,
  AlertCircle,
  Loader,
  RefreshCw,
  Download,
  Upload,
  FileSpreadsheet,
  Printer,
  Trash2 as TrashIcon,
  HelpCircle,
  Info
} from 'lucide-react';
import api from '../api';

const Books = () => {
  const navigate = useNavigate();

  // Primary State
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [categories, setCategories] = useState([]);

  // Modals visibility state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [showBarcodeGeneratorModal, setShowBarcodeGeneratorModal] = useState(false);

  // Selected Records
  const [selectedBook, setSelectedBook] = useState(null);
  const [selectedBooks, setSelectedBooks] = useState([]);
  const [selectedBooksForBarcode, setSelectedBooksForBarcode] = useState([]);

  // Pagination
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    isbn: '',
    publisher: '',
    publicationYear: '',
    edition: '',
    category: 'Other',
    subject: '',
    shelfLocation: '',
    rackNumber: '',
    totalCopies: 1,
    description: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Barcode Generation State
  const [barcodePreset, setBarcodePreset] = useState('24_up');
  const [barcodeColumns, setBarcodeColumns] = useState(3);
  const [includeTitle, setIncludeTitle] = useState(true);
  const [includeText, setIncludeText] = useState(true);
  const [generatingBarcodes, setGeneratingBarcodes] = useState(false);
  const [barcodeProgress, setBarcodeProgress] = useState(0);

  // Bulk Upload State
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkValidating, setBulkValidating] = useState(false);
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkValidationResult, setBulkValidationResult] = useState(null);
  const [bulkProcessResult, setBulkProcessResult] = useState(null);
  const [bulkError, setBulkError] = useState(null);
  const [, setBulkSuccess] = useState(null);
  const [bulkStep, setBulkStep] = useState(1);

  // Universal Custom Notification / Dialog State
  const [notification, setNotification] = useState({
    isOpen: false,
    type: 'info', // 'success' | 'error' | 'warning' | 'confirm'
    title: '',
    message: '',
    onConfirm: null,
    confirmText: 'OK',
    cancelText: 'Cancel'
  });

  // Action Loading States
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [individualBarcodeLoadingId, setIndividualBarcodeLoadingId] = useState(null);

  const searchRef = useRef(null);
  const fileInputRef = useRef(null);

  const tenantId = localStorage.getItem('tenantId');

  // Helper function to safely get Book ID regardless of format
  const getBookId = (book) => {
    if (!book) return null;
    if (typeof book === 'string') return book;
    if (book._id) {
      if (typeof book._id === 'string') return book._id;
      if (book._id.$oid) return book._id.$oid;
    }
    if (book.id) return book.id;
    return null;
  };

  // Helper to extract master barcode safely for display
  const getMasterBarcodeText = (book) => {
    if (!book) return 'N/A';
    if (book.masterBarcode && book.masterBarcode !== 'undefined' && book.masterBarcode !== 'null') {
      return book.masterBarcode;
    }
    if (Array.isArray(book.copies) && book.copies.length > 0 && book.copies[0]?.barcode) {
      return book.copies[0].barcode.split('-')[0];
    }
    if (book.barcode && book.barcode !== 'undefined' && book.barcode !== 'null') {
      return book.barcode.split('-')[0];
    }
    return 'N/A';
  };

  // Trigger Custom Notification Modal
  const showNotice = ({ type = 'info', title, message, onConfirm = null, confirmText = 'OK', cancelText = 'Cancel' }) => {
    setNotification({
      isOpen: true,
      type,
      title,
      message,
      onConfirm,
      confirmText,
      cancelText
    });
  };

  const closeNotice = () => {
    setNotification(prev => ({ ...prev, isOpen: false }));
  };

  // Fetch paginated books list
  const fetchBooks = useCallback(async (page = 1, search = searchTerm) => {
    setLoading(true);
    setError(null);

    try {
      const params = {
        tenantId,
        page,
        limit: pagination.limit
      };

      if (search) params.search = search;
      if (selectedCategory !== 'all') params.category = selectedCategory;
      if (selectedStatus === 'available') params.available = 'true';

      const response = await api.get('/api/books', { params });

      if (response.data.success) {
        setBooks(response.data.data);
        setPagination(prev => ({
          ...prev,
          page,
          total: response.data.pagination?.total || response.data.data.length,
          pages: response.data.pagination?.pages || 1
        }));
      }
    } catch (err) {
      console.error('Error fetching books:', err);
      setError(err.response?.data?.message || 'Unable to load books. Please check your network connection.');
    } finally {
      setLoading(false);
    }
  }, [tenantId, pagination.limit, selectedCategory, selectedStatus, searchTerm]);

  // Fetch all books for bulk barcode modal
  const fetchAllBooks = useCallback(async () => {
    try {
      const response = await api.get('/api/books', {
        params: {
          tenantId,
          page: 1,
          limit: 10000,
          sortBy: 'title',
          sortOrder: 'asc'
        },
        timeout: 60000
      });

      if (response.data.success) {
        return response.data.data;
      }
      return [];
    } catch (err) {
      console.error('Error fetching all books:', err);
      return [];
    }
  }, [tenantId]);

  // Fetch category list
  const fetchCategories = useCallback(async () => {
    try {
      const response = await api.get('/api/books/categories', { params: { tenantId } });
      if (response.data.success) {
        setCategories(response.data.data);
      }
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  }, [tenantId]);

  // Fetch search suggestions
  const getSearchSuggestions = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setSearchSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    try {
      const response = await api.get('/api/books/suggestions', {
        params: { tenantId, query, limit: 5 }
      });

      if (response.data.success) {
        setSearchSuggestions(response.data.data);
        setShowSuggestions(response.data.data.length > 0);
      }
    } catch (err) {
      console.error('Error fetching suggestions:', err);
    }
  }, [tenantId]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm.length > 0) {
        getSearchSuggestions(searchTerm);
      } else {
        setSearchSuggestions([]);
        setShowSuggestions(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm, getSearchSuggestions]);

  const handleSearch = (e) => {
    e.preventDefault();
    setShowSuggestions(false);
    fetchBooks(1, searchTerm);
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchTerm(suggestion.title);
    setShowSuggestions(false);
    fetchBooks(1, suggestion.title);
  };

  const clearSearch = () => {
    setSearchTerm('');
    setSearchSuggestions([]);
    setShowSuggestions(false);
    fetchBooks(1, '');
  };

  const resetForm = () => {
    setFormData({
      title: '',
      author: '',
      isbn: '',
      publisher: '',
      publicationYear: '',
      edition: '',
      category: 'Other',
      subject: '',
      shelfLocation: '',
      rackNumber: '',
      totalCopies: 1,
      description: ''
    });
    setFormErrors({});
  };

  const openEditModal = (book) => {
    setSelectedBook(book);
    setFormData({
      title: book.title || '',
      author: book.author || '',
      isbn: book.isbn || '',
      publisher: book.publisher || '',
      publicationYear: book.publicationYear || '',
      edition: book.edition || '',
      category: book.category || 'Other',
      subject: book.subject || '',
      shelfLocation: book.shelfLocation || '',
      rackNumber: book.rackNumber || '',
      totalCopies: book.totalCopies || 1,
      description: book.description || ''
    });
    setShowEditModal(true);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'totalCopies' ? parseInt(value) || 1 : value
    }));
    if (formErrors[name]) {
      setFormErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  // Add Book Handler
  const handleAddBook = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormErrors({});

    try {
      const response = await api.post('/api/books', {
        ...formData,
        tenantId
      });

      if (response.data.success) {
        await fetchBooks(pagination.page);
        setShowAddModal(false);
        resetForm();
        showNotice({
          type: 'success',
          title: 'Book Added',
          message: 'The new book record was created successfully.'
        });
      }
    } catch (err) {
      if (err.response?.data?.errors) {
        const errors = {};
        err.response.data.errors.forEach(e => {
          errors[e.field] = e.message;
        });
        setFormErrors(errors);
      } else {
        setFormErrors({ submit: err.response?.data?.message || 'Failed to add book.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Update Book Handler
  const handleUpdateBook = async (e) => {
    e.preventDefault();
    const id = getBookId(selectedBook);
    if (!id) {
      showNotice({ type: 'error', title: 'Error', message: 'Unable to resolve book ID for update.' });
      return;
    }

    setSubmitting(true);
    setFormErrors({});

    try {
      const response = await api.put(`/api/books/${id}`, {
        ...formData,
        tenantId
      });

      if (response.data.success) {
        await fetchBooks(pagination.page);
        setShowEditModal(false);
        setSelectedBook(null);
        resetForm();
        showNotice({
          type: 'success',
          title: 'Book Updated',
          message: 'The book information was successfully updated.'
        });
      }
    } catch (err) {
      if (err.response?.data?.errors) {
        const errors = {};
        err.response.data.errors.forEach(e => {
          errors[e.field] = e.message;
        });
        setFormErrors(errors);
      } else {
        setFormErrors({ submit: err.response?.data?.message || 'Failed to update book.' });
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Delete Book Confirmation
  const handleDeleteBookClick = (book) => {
    const id = getBookId(book);
    if (!id) return;

    showNotice({
      type: 'confirm',
      title: 'Delete Book',
      message: `Are you sure you want to delete "${book.title}"? This cannot be undone.`,
      confirmText: 'Yes, Delete',
      onConfirm: () => executeDeleteBook(id)
    });
  };

  const executeDeleteBook = async (bookId) => {
    try {
      const response = await api.delete(`/api/books/${bookId}`, {
        data: { tenantId }
      });

      if (response.data.success) {
        await fetchBooks(pagination.page);
        showNotice({
          type: 'success',
          title: 'Deleted',
          message: 'The book has been removed from the library.'
        });
      }
    } catch (err) {
      showNotice({
        type: 'error',
        title: 'Delete Failed',
        message: err.response?.data?.message || 'Unable to delete book.'
      });
    }
  };

  // Bulk Delete
  const handleBulkDelete = async () => {
    if (selectedBooks.length === 0) return;

    setBulkDeleting(true);
    try {
      const response = await api.post('/api/books/bulk/delete', {
        tenantId,
        bookIds: selectedBooks
      });

      if (response.data.success) {
        setSelectedBooks([]);
        setShowBulkDeleteModal(false);
        await fetchBooks(pagination.page);
        showNotice({
          type: 'success',
          title: 'Bulk Delete Complete',
          message: `Successfully removed ${response.data.data?.deletedCount || selectedBooks.length} book(s).`
        });
      }
    } catch (err) {
      showNotice({
        type: 'error',
        title: 'Bulk Delete Error',
        message: err.response?.data?.message || 'Failed to delete selected books.'
      });
    } finally {
      setBulkDeleting(false);
    }
  };

  // Print Individual Book Barcode
  const handleIndividualBarcode = async (book) => {
    const id = getBookId(book);
    if (!book || !id) {
      showNotice({
        type: 'error',
        title: 'Selection Error',
        message: 'Invalid book selected. Please refresh the page and try again.'
      });
      return;
    }

    setIndividualBarcodeLoadingId(id);

    try {
      const response = await api.post('/api/books/barcode-labels', {
        tenantId,
        bookIds: [id],
        books: [{
          _id: id,
          title: book.title,
          author: book.author,
          masterBarcode: getMasterBarcodeText(book),
          totalCopies: book.totalCopies || 1,
          category: book.category,
          copies: book.copies || []
        }],
        presetType: '24_up',
        columns: 3,
        includeText: true,
        includeTitle: true,
        perCopy: true
      }, {
        responseType: 'blob',
        timeout: 30000
      });

      // Download PDF File
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const cleanTitle = (book.title || 'book').replace(/[^a-zA-Z0-9]/g, '_');
      link.setAttribute('download', `sticker_barcodes_${cleanTitle}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showNotice({
        type: 'success',
        title: 'Barcodes Downloaded',
        message: `Sticker barcode PDF generated for "${book.title}". Print at 100% scale on A4 sticker sheets.`
      });

    } catch (err) {
      console.error('Error generating individual barcode:', err);
      showNotice({
        type: 'error',
        title: 'PDF Generation Failed',
        message: err.response?.data?.message || err.message || 'Unable to generate barcode PDF.'
      });
    } finally {
      setIndividualBarcodeLoadingId(null);
    }
  };

  // Generate Bulk Barcode Labels
  const handleGenerateBarcodes = async () => {
    if (selectedBooksForBarcode.length === 0) {
      showNotice({
        type: 'warning',
        title: 'No Books Selected',
        message: 'Please choose at least one book to generate barcode stickers.'
      });
      return;
    }

    setGeneratingBarcodes(true);
    setBarcodeProgress(0);
    setError(null);

    try {
      const selectedBooksData = books.filter(b => {
        const id = getBookId(b);
        return selectedBooksForBarcode.includes(id);
      });

      if (selectedBooksData.length === 0) {
        showNotice({
          type: 'error',
          title: 'Selection Error',
          message: 'No valid books found for the selected IDs.'
        });
        setGeneratingBarcodes(false);
        return;
      }

      const cleanBooksPayload = selectedBooksData.map(book => ({
        _id: getBookId(book),
        title: book.title,
        author: book.author,
        masterBarcode: getMasterBarcodeText(book),
        totalCopies: book.totalCopies || 1,
        category: book.category,
        isbn: book.isbn,
        copies: book.copies || []
      }));

      const response = await api.post('/api/books/barcode-labels', {
        tenantId,
        bookIds: selectedBooksForBarcode,
        books: cleanBooksPayload,
        presetType: barcodePreset,
        columns: barcodeColumns,
        includeText: includeText,
        includeTitle: includeTitle,
        perCopy: true
      }, {
        responseType: 'blob',
        timeout: 90000,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setBarcodeProgress(percent);
          }
        }
      });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `barcode_stickers_${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setSelectedBooksForBarcode([]);
      setShowBarcodeGeneratorModal(false);
      setBarcodeProgress(100);

      showNotice({
        type: 'success',
        title: 'Stickers Ready',
        message: 'Barcode sticker sheet generated! Remember to select "Actual Size (100%)" when printing.'
      });

    } catch (err) {
      console.error('Error generating barcodes:', err);
      const msg = err.response?.data?.message || err.message || 'Failed to generate barcode PDF.';
      setError(msg);
      showNotice({
        type: 'error',
        title: 'Generation Failed',
        message: msg
      });
    } finally {
      setGeneratingBarcodes(false);
      setBarcodeProgress(0);
    }
  };

  const openBarcodeGenerator = async () => {
    setLoading(true);
    const allBooks = await fetchAllBooks();
    setBooks(allBooks.length > 0 ? allBooks : books);
    setSelectedBooksForBarcode([]);
    setBarcodeProgress(0);
    setError(null);
    setLoading(false);
    setShowBarcodeGeneratorModal(true);
  };

  // Checkbox Selectors
  const toggleBookSelection = (book) => {
    const id = getBookId(book);
    if (!id) return;

    setSelectedBooks(prev => {
      if (prev.includes(id)) {
        return prev.filter(item => item !== id);
      } else {
        return [...prev, id];
      }
    });
  };

  const toggleAllBooks = () => {
    if (selectedBooks.length === books.length) {
      setSelectedBooks([]);
    } else {
      setSelectedBooks(books.map(b => getBookId(b)).filter(Boolean));
    }
  };

  // Bulk Upload logic
  const downloadTemplate = async () => {
    try {
      const response = await api.get('/api/books/template', {
        params: { tenantId },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bulk_upload_template_${tenantId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setBulkSuccess('Template excel file downloaded successfully.');
      setTimeout(() => setBulkSuccess(null), 4000);
    } catch (err) {
      console.error('Error downloading template:', err);
      setBulkError('Failed to download template. Please try again.');
    }
  };

  const handleBulkFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-excel'];
      if (!allowedTypes.includes(file.type)) {
        setBulkError('Please select a valid Excel file (.xlsx or .xls)');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setBulkError('File size is larger than 5MB.');
        return;
      }
      setBulkFile(file);
      setBulkError(null);
      setBulkValidationResult(null);
      setBulkProcessResult(null);
      setBulkStep(1);
    }
  };

  const removeBulkFile = () => {
    setBulkFile(null);
    setBulkValidationResult(null);
    setBulkProcessResult(null);
    setBulkStep(1);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const validateBulkFile = async () => {
    if (!bulkFile) {
      setBulkError('Please select an Excel file first.');
      return;
    }

    setBulkValidating(true);
    setBulkError(null);

    const formDataUpload = new FormData();
    formDataUpload.append('file', bulkFile);
    formDataUpload.append('tenantId', tenantId);

    try {
      const response = await api.post('/api/books/bulk/validate', formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setBulkValidationResult(response.data.data);
        setBulkStep(2);
      }
    } catch (err) {
      console.error('Validation error:', err);
      setBulkError(err.response?.data?.message || 'Failed to validate Excel file.');
    } finally {
      setBulkValidating(false);
    }
  };

  const processBulkBooks = async () => {
    if (!bulkValidationResult || !bulkValidationResult.validBooks || bulkValidationResult.validBooks.length === 0) {
      setBulkError('No valid book entries to import.');
      return;
    }

    setBulkProcessing(true);
    setBulkError(null);

    try {
      const response = await api.post('/api/books/bulk/process', {
        tenantId,
        books: bulkValidationResult.validBooks
      });

      if (response.data.success) {
        setBulkProcessResult(response.data.data);
        setBulkStep(3);
        fetchBooks(pagination.page);
      }
    } catch (err) {
      console.error('Processing error:', err);
      setBulkError(err.response?.data?.message || 'Failed to import books.');
    } finally {
      setBulkProcessing(false);
    }
  };

  const resetBulkUpload = () => {
    setBulkFile(null);
    setBulkValidationResult(null);
    setBulkProcessResult(null);
    setBulkStep(1);
    setBulkError(null);
    setBulkSuccess(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    setShowBulkUploadModal(false);
  };

  const getStatusBadge = (book) => {
    const available = book.availableCopies ?? 0;
    const total = book.totalCopies ?? 1;

    if (available > 0) {
      return {
        label: `${available} Available`,
        color: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800'
      };
    } else if (total > 0 && available === 0) {
      return {
        label: 'All Issued',
        color: 'bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border border-amber-200 dark:border-amber-800'
      };
    } else {
      return {
        label: 'Unavailable',
        color: 'bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 border border-rose-200 dark:border-rose-800'
      };
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (tenantId) {
      fetchBooks();
      fetchCategories();
    }
  }, [tenantId, fetchBooks, fetchCategories]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (tenantId) fetchBooks(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [selectedCategory, selectedStatus, tenantId, fetchBooks]);

  if (loading && books.length === 0) {
    return (
      <div className="min-h-[50vh] flex flex-col items-center justify-center p-6 text-center">
        <Loader className="h-9 w-9 text-blue-600 animate-spin mb-3" />
        <p className="text-gray-600 dark:text-gray-300 font-medium">Loading library catalog...</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 py-4 space-y-6">

      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Book className="h-6 w-6 text-blue-600 dark:text-blue-400" />
            Book Catalog
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Manage titles, track copies, and print barcode stickers
          </p>
        </div>

        {/* Action Toolbar */}
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="flex-1 sm:flex-none min-h-[42px] px-3.5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center space-x-1.5 shadow-sm"
          >
            <Plus className="h-4 w-4" />
            <span>Add Book</span>
          </button>

          <button
            onClick={() => { resetBulkUpload(); setShowBulkUploadModal(true); }}
            className="flex-1 sm:flex-none min-h-[42px] px-3.5 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center space-x-1.5 shadow-sm"
          >
            <Upload className="h-4 w-4" />
            <span>Excel Upload</span>
          </button>

          <button
            onClick={openBarcodeGenerator}
            className="flex-1 sm:flex-none min-h-[42px] px-3.5 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center space-x-1.5 shadow-sm"
            disabled={generatingBarcodes}
          >
            <Printer className="h-4 w-4" />
            <span>Print Stickers</span>
          </button>

          {selectedBooks.length > 0 && (
            <button
              onClick={() => setShowBulkDeleteModal(true)}
              className="w-full sm:w-auto min-h-[42px] px-3.5 py-2 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-sm font-medium transition-colors flex items-center justify-center space-x-1.5 shadow-sm"
            >
              <TrashIcon className="h-4 w-4" />
              <span>Delete ({selectedBooks.length})</span>
            </button>
          )}
        </div>
      </div>

      {/* Search & Filters Controls */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl p-4 shadow-sm border border-gray-200/80 dark:border-gray-700 space-y-3">
        <div className="flex flex-col md:flex-row gap-3">
          
          {/* Search Bar with Autocomplete */}
          <div className="flex-1 relative" ref={searchRef}>
            <form onSubmit={handleSearch} className="relative">
              <Search className="absolute left-3.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title, author, ISBN, barcode..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full min-h-[44px] pl-10 pr-10 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 p-1"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </form>

            {/* Suggestions Dropdown */}
            {showSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 z-50 max-h-64 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                {searchSuggestions.map((suggestion) => (
                  <button
                    key={suggestion.id || suggestion._id}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className="w-full text-left px-4 py-2.5 hover:bg-blue-50 dark:hover:bg-gray-700/60 transition-colors"
                  >
                    <p className="text-sm font-medium text-gray-900 dark:text-white truncate">{suggestion.title}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {suggestion.author} • {suggestion.category || 'Other'} • Barcode: {suggestion.barcode || 'N/A'}
                    </p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Filter Dropdowns */}
          <div className="flex flex-wrap sm:flex-nowrap gap-2">
            <select
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
              className="flex-1 sm:w-44 min-h-[44px] px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.name || cat} value={cat.name || cat}>{cat.name || cat}</option>
              ))}
            </select>

            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="flex-1 sm:w-40 min-h-[44px] px-3 py-2 rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Status</option>
              <option value="available">Available</option>
            </select>

            <button
              onClick={() => fetchBooks(1)}
              className="min-h-[44px] px-3.5 py-2 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 rounded-xl text-sm font-medium transition-colors flex items-center justify-center space-x-1.5"
              title="Refresh Catalog"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>

        {/* Bulk Selection Bar */}
        {books.length > 0 && (
          <div className="flex items-center justify-between pt-2 border-t border-gray-100 dark:border-gray-700 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            <label className="flex items-center space-x-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={selectedBooks.length === books.length && books.length > 0}
                onChange={toggleAllBooks}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span>Select all on page ({books.length} books)</span>
            </label>
            {selectedBooks.length > 0 && (
              <span className="font-medium text-blue-600 dark:text-blue-400">
                {selectedBooks.length} selected
              </span>
            )}
          </div>
        )}
      </div>

      {/* Main Books Grid */}
      {books.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {books.map((book) => {
            const bookId = getBookId(book);
            const status = getStatusBadge(book);
            const isSelected = selectedBooks.includes(bookId);
            const masterBarcode = getMasterBarcodeText(book);
            const isIndividualLoading = individualBarcodeLoadingId === bookId;

            return (
              <div
                key={bookId || Math.random()}
                className={`bg-white dark:bg-gray-800 rounded-2xl p-4 sm:p-5 shadow-sm border transition-all flex flex-col justify-between ${
                  isSelected
                    ? 'border-blue-500 ring-2 ring-blue-500/20 dark:border-blue-500'
                    : 'border-gray-200/80 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                }`}
              >
                <div>
                  {/* Card Top Badges & Actions */}
                  <div className="flex items-start justify-between gap-2 mb-3">
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleBookSelection(book)}
                        className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 mt-0.5"
                      />
                      <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                        {book.category || 'Other'}
                      </span>
                      <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-full ${status.color}`}>
                        {status.label}
                      </span>
                    </div>

                    {/* Individual Icon Actions */}
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => handleIndividualBarcode(book)}
                        disabled={isIndividualLoading}
                        className="p-2 rounded-lg hover:bg-emerald-50 dark:hover:bg-emerald-900/20 text-gray-500 dark:text-gray-400 hover:text-emerald-600 transition-colors disabled:opacity-50"
                        title="Print Barcode Stickers for all copies"
                      >
                        {isIndividualLoading ? (
                          <Loader className="h-4 w-4 animate-spin text-emerald-600" />
                        ) : (
                          <Printer className="h-4 w-4" />
                        )}
                      </button>

                      <button
                        onClick={() => openEditModal(book)}
                        className="p-2 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-gray-500 dark:text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit Book"
                      >
                        <Edit className="h-4 w-4" />
                      </button>

                      <button
                        onClick={() => handleDeleteBookClick(book)}
                        className="p-2 rounded-lg hover:bg-rose-50 dark:hover:bg-rose-900/20 text-gray-500 dark:text-gray-400 hover:text-rose-600 transition-colors"
                        title="Delete Book"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Book Info */}
                  <h3 className="text-base font-bold text-gray-900 dark:text-white line-clamp-2 mb-1">
                    {book.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400 mb-3">
                    by {book.author || 'Unknown Author'}
                  </p>

                  <div className="space-y-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-700/30 p-2.5 rounded-xl mb-4">
                    <div className="flex justify-between">
                      <span>Copies: <strong>{book.availableCopies ?? 0}/{book.totalCopies || 1}</strong></span>
                      <span>Shelf: <strong>{book.shelfLocation || 'N/A'}</strong></span>
                    </div>
                    {book.isbn && <div className="truncate">ISBN: <span className="font-mono">{book.isbn}</span></div>}
                    <div className="truncate font-mono">Master Barcode: <strong className="text-gray-700 dark:text-gray-200">{masterBarcode}</strong></div>
                  </div>
                </div>

                {/* Circulation Buttons */}
                <div className="flex space-x-2 pt-2 border-t border-gray-100 dark:border-gray-700/80">
                  <button
                    onClick={() => navigate(`/librarian/transactions/issue?bookId=${bookId}`)}
                    className="flex-1 min-h-[36px] px-3 py-1.5 text-xs font-medium bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 rounded-xl hover:bg-blue-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                    disabled={(book.availableCopies ?? 0) <= 0}
                  >
                    Issue
                  </button>
                  <button
                    onClick={() => navigate(`/librarian/transactions/return?bookId=${bookId}`)}
                    className="flex-1 min-h-[36px] px-3 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 rounded-xl hover:bg-emerald-100 transition-colors"
                  >
                    Return
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* Empty State */
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 sm:p-12 text-center border border-gray-200/80 dark:border-gray-700">
          <Book className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
          <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white">No Books Found</h3>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-sm mx-auto">
            We couldn't find any books matching your criteria. Try adjusting your search query or add a new record.
          </p>
          <button
            onClick={() => { resetForm(); setShowAddModal(true); }}
            className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl transition-colors inline-flex items-center space-x-1.5"
          >
            <Plus className="h-4 w-4" />
            <span>Add Book</span>
          </button>
        </div>
      )}

      {/* Pagination Bar */}
      {pagination.pages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <span className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
            Page {pagination.page} of {pagination.pages} ({pagination.total} total books)
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => fetchBooks(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="px-3 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 text-xs sm:text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => fetchBooks(pagination.page + 1)}
              disabled={pagination.page >= pagination.pages}
              className="px-3 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 text-xs sm:text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* ==================== ADD BOOK MODAL ==================== */}
      {showAddModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Add New Book</h2>
              <button onClick={() => setShowAddModal(false)} className="p-1.5 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleAddBook} className="p-4 sm:p-6 overflow-y-auto space-y-4">
              {formErrors.submit && (
                <div className="p-3 bg-rose-50 text-rose-700 rounded-xl text-xs sm:text-sm border border-rose-200">
                  {formErrors.submit}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Book Title *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" required />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Author *</label>
                  <input type="text" name="author" value={formData.author} onChange={handleInputChange} className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" required />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">ISBN</label>
                  <input type="text" name="isbn" value={formData.isbn} onChange={handleInputChange} className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Publisher</label>
                  <input type="text" name="publisher" value={formData.publisher} onChange={handleInputChange} className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Category</label>
                  <select name="category" value={formData.category} onChange={handleInputChange} className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white">
                    <option value="Textbook">Textbook</option>
                    <option value="Reference">Reference</option>
                    <option value="Fiction">Fiction</option>
                    <option value="Non-Fiction">Non-Fiction</option>
                    <option value="Journal">Journal</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Total Copies *</label>
                  <input type="number" name="totalCopies" value={formData.totalCopies} onChange={handleInputChange} min="1" className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" required />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
                <button type="button" onClick={() => setShowAddModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
                <button type="submit" disabled={submitting} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center space-x-1.5">
                  {submitting && <Loader className="h-4 w-4 animate-spin" />}
                  <span>Save Book</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================== EDIT BOOK MODAL ==================== */}
      {showEditModal && selectedBook && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800">
              <h2 className="text-lg font-bold text-gray-900 dark:text-white">Edit Book</h2>
              <button onClick={() => setShowEditModal(false)} className="p-1.5 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleUpdateBook} className="p-4 sm:p-6 overflow-y-auto space-y-4">
              {formErrors.submit && (
                <div className="p-3 bg-rose-50 text-rose-700 rounded-xl text-xs sm:text-sm border border-rose-200">
                  {formErrors.submit}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Book Title *</label>
                  <input type="text" name="title" value={formData.title} onChange={handleInputChange} className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" required />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Author *</label>
                  <input type="text" name="author" value={formData.author} onChange={handleInputChange} className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" required />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">ISBN</label>
                  <input type="text" name="isbn" value={formData.isbn} onChange={handleInputChange} className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">Total Copies *</label>
                  <input type="number" name="totalCopies" value={formData.totalCopies} onChange={handleInputChange} min="1" className="w-full px-3 py-2 text-sm rounded-xl border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50 text-gray-900 dark:text-white" required />
                </div>
              </div>

              <div className="pt-4 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-2">
                <button type="button" onClick={() => setShowEditModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-xl">Cancel</button>
                <button type="submit" disabled={submitting} className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center space-x-1.5">
                  {submitting && <Loader className="h-4 w-4 animate-spin" />}
                  <span>Update Changes</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ==================== BULK DELETE CONFIRM MODAL ==================== */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
            <div className="flex items-center space-x-3 text-rose-600 dark:text-rose-400">
              <AlertCircle className="h-6 w-6" />
              <h3 className="text-lg font-bold">Confirm Delete</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Are you sure you want to delete <strong>{selectedBooks.length}</strong> selected book(s)? This action cannot be reversed.
            </p>
            <div className="flex space-x-2 pt-2">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                className="flex-1 py-2 rounded-xl border border-gray-300 dark:border-gray-600 text-sm font-medium hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleting}
                className="flex-1 py-2 rounded-xl bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium transition-colors flex items-center justify-center space-x-1.5 disabled:opacity-50"
              >
                {bulkDeleting ? <Loader className="h-4 w-4 animate-spin" /> : <span>Delete Now</span>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== STICKER BARCODE GENERATOR MODAL ==================== */}
      {showBarcodeGeneratorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between bg-gray-50/50 dark:bg-gray-800">
              <div className="flex items-center space-x-2">
                <Printer className="h-5 w-5 text-emerald-600" />
                <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Print Sticker Barcodes</h2>
              </div>
              <button
                onClick={() => setShowBarcodeGeneratorModal(false)}
                disabled={generatingBarcodes}
                className="p-1.5 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto space-y-5">
              
              {/* Select Books Selector */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-xs font-semibold text-gray-700 dark:text-gray-300">Choose Books to Print</label>
                  <div className="space-x-2 text-xs">
                    <button
                      onClick={() => setSelectedBooksForBarcode(books.map(b => getBookId(b)).filter(Boolean))}
                      disabled={generatingBarcodes}
                      className="text-blue-600 hover:underline"
                    >
                      Select All ({books.length})
                    </button>
                    <button
                      onClick={() => setSelectedBooksForBarcode([])}
                      disabled={generatingBarcodes}
                      className="text-rose-600 hover:underline"
                    >
                      Clear
                    </button>
                  </div>
                </div>

                <div className="border border-gray-200 dark:border-gray-700 rounded-xl max-h-48 overflow-y-auto p-2 space-y-1 bg-gray-50/50 dark:bg-gray-700/20">
                  {books.map((book) => {
                    const id = getBookId(book);
                    const isChecked = selectedBooksForBarcode.includes(id);

                    return (
                      <label
                        key={id || Math.random()}
                        className="flex items-center space-x-2.5 p-2 rounded-lg hover:bg-white dark:hover:bg-gray-700 cursor-pointer text-xs sm:text-sm select-none"
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => {
                            if (generatingBarcodes) return;
                            setSelectedBooksForBarcode(prev =>
                              prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
                            );
                          }}
                          disabled={generatingBarcodes}
                          className="w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500"
                        />
                        <span className="font-medium text-gray-900 dark:text-white flex-1 truncate">{book.title}</span>
                        <span className="text-gray-500 font-mono text-xs">{getMasterBarcodeText(book)}</span>
                        <span className="text-gray-400 text-xs">({book.totalCopies || 1} copies)</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Layout Preset Picker */}
              <div>
                <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">A4 Sticker Sheet Format</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: '24_up', name: '24 Labels', desc: '3 x 8 Grid (70x37mm)' },
                    { id: '40_up', name: '40 Labels', desc: '4 x 10 Grid (48x25mm)' },
                    { id: '65_up', name: '65 Labels', desc: '5 x 13 Grid (38x21mm)' }
                  ].map(preset => (
                    <button
                      key={preset.id}
                      type="button"
                      onClick={() => {
                        setBarcodePreset(preset.id);
                        if (preset.id === '24_up') setBarcodeColumns(3);
                        if (preset.id === '40_up') setBarcodeColumns(4);
                        if (preset.id === '65_up') setBarcodeColumns(5);
                      }}
                      disabled={generatingBarcodes}
                      className={`p-3 rounded-xl border text-left transition-all ${
                        barcodePreset === preset.id
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 ring-2 ring-emerald-500/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <p className="text-xs font-bold">{preset.name}</p>
                      <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-0.5">{preset.desc}</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Include Options */}
              <div className="flex flex-wrap gap-4 pt-1">
                <label className="flex items-center space-x-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeTitle}
                    onChange={(e) => setIncludeTitle(e.target.checked)}
                    disabled={generatingBarcodes}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded"
                  />
                  <span>Include Book Title</span>
                </label>

                <label className="flex items-center space-x-2 text-xs sm:text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={includeText}
                    onChange={(e) => setIncludeText(e.target.checked)}
                    disabled={generatingBarcodes}
                    className="w-4 h-4 text-emerald-600 border-gray-300 rounded"
                  />
                  <span>Include Barcode Number</span>
                </label>
              </div>

              {/* Printing Help Callout */}
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-3 flex items-start space-x-2 text-xs text-amber-800 dark:text-amber-300">
                <Info className="h-4 w-4 shrink-0 mt-0.5" />
                <p>When printing PDF on sticker paper, set printer scale to <strong>Actual Size (100%)</strong>. Do not use "Fit to Page".</p>
              </div>

              {/* Progress Indicator */}
              {generatingBarcodes && (
                <div className="space-y-2">
                  <div className="flex justify-between text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                    <span>Creating PDF stickers...</span>
                    <span>{barcodeProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-emerald-600 h-2 transition-all duration-200"
                      style={{ width: `${barcodeProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800 flex space-x-2">
              <button
                onClick={() => setShowBarcodeGeneratorModal(false)}
                disabled={generatingBarcodes}
                className="flex-1 min-h-[42px] px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateBarcodes}
                disabled={selectedBooksForBarcode.length === 0 || generatingBarcodes}
                className="flex-1 min-h-[42px] px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-xl transition-colors disabled:opacity-50 flex items-center justify-center space-x-1.5"
              >
                {generatingBarcodes ? (
                  <Loader className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Printer className="h-4 w-4" />
                    <span>Download PDF</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ==================== BULK UPLOAD MODAL ==================== */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h2 className="text-base sm:text-lg font-bold text-gray-900 dark:text-white">Excel Bulk Upload</h2>
              <button onClick={resetBulkUpload} className="p-1.5 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-4 sm:p-6 overflow-y-auto space-y-4">
              {/* Download Template button */}
              <div className="flex justify-between items-center">
                <span className="text-xs text-gray-500">Step {bulkStep} of 3</span>
                <button
                  onClick={downloadTemplate}
                  className="px-3 py-1.5 bg-emerald-50 text-emerald-700 border border-emerald-200 text-xs font-semibold rounded-xl hover:bg-emerald-100 flex items-center space-x-1"
                >
                  <Download className="h-3.5 w-3.5" />
                  <span>Download Sample Excel</span>
                </button>
              </div>

              {bulkError && (
                <div className="p-3 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs sm:text-sm">
                  {bulkError}
                </div>
              )}

              {/* Step 1: Choose File */}
              {bulkStep === 1 && (
                <div className="space-y-4">
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-2xl p-8 text-center cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 bg-gray-50/50 dark:bg-gray-700/20"
                  >
                    <FileSpreadsheet className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                      {bulkFile ? bulkFile.name : 'Click to select Excel sheet (.xlsx)'}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".xlsx, .xls"
                      onChange={handleBulkFileSelect}
                      className="hidden"
                    />
                  </div>

                  <button
                    onClick={validateBulkFile}
                    disabled={!bulkFile || bulkValidating}
                    className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-xl disabled:opacity-50 flex items-center justify-center space-x-1.5"
                  >
                    {bulkValidating ? <Loader className="h-4 w-4 animate-spin" /> : <span>Validate Excel Data</span>}
                  </button>
                </div>
              )}

              {/* Step 2: Validation */}
              {bulkStep === 2 && bulkValidationResult && (
                <div className="space-y-4">
                  <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl text-xs sm:text-sm">
                    Found <strong>{bulkValidationResult.validBooks?.length || 0}</strong> valid book rows ready to import.
                  </div>

                  <div className="flex space-x-2">
                    <button onClick={removeBulkFile} className="flex-1 py-2 border rounded-xl text-xs font-medium">Re-upload</button>
                    <button
                      onClick={processBulkBooks}
                      disabled={bulkProcessing}
                      className="flex-1 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-medium flex items-center justify-center space-x-1"
                    >
                      {bulkProcessing ? <Loader className="h-4 w-4 animate-spin" /> : <span>Start Import</span>}
                    </button>
                  </div>
                </div>
              )}

              {/* Step 3: Success */}
              {bulkStep === 3 && bulkProcessResult && (
                <div className="text-center py-4 space-y-3">
                  <CheckCircle className="h-12 w-12 text-emerald-500 mx-auto" />
                  <h3 className="text-base font-bold text-gray-900 dark:text-white">Import Successful!</h3>
                  <p className="text-xs text-gray-500">
                    Added {bulkProcessResult.success?.length || 0} books to the catalog.
                  </p>
                  <button onClick={resetBulkUpload} className="px-6 py-2 bg-blue-600 text-white rounded-xl text-xs font-medium">
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ==================== REUSABLE NOTIFICATION / DIALOG MODAL ==================== */}
      {notification.isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-sm w-full p-5 space-y-4">
            <div className="flex items-center space-x-3">
              {notification.type === 'success' && <CheckCircle className="h-6 w-6 text-emerald-500 shrink-0" />}
              {notification.type === 'error' && <AlertCircle className="h-6 w-6 text-rose-500 shrink-0" />}
              {notification.type === 'warning' && <AlertCircle className="h-6 w-6 text-amber-500 shrink-0" />}
              {notification.type === 'confirm' && <HelpCircle className="h-6 w-6 text-blue-500 shrink-0" />}
              {notification.type === 'info' && <Info className="h-6 w-6 text-blue-500 shrink-0" />}
              <h3 className="text-base font-bold text-gray-900 dark:text-white">{notification.title}</h3>
            </div>

            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
              {notification.message}
            </p>

            <div className="flex justify-end space-x-2 pt-2">
              {notification.type === 'confirm' ? (
                <>
                  <button
                    onClick={closeNotice}
                    className="px-3.5 py-1.5 rounded-xl border border-gray-300 dark:border-gray-600 text-xs font-medium hover:bg-gray-100"
                  >
                    {notification.cancelText}
                  </button>
                  <button
                    onClick={() => {
                      if (notification.onConfirm) notification.onConfirm();
                      closeNotice();
                    }}
                    className="px-3.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium"
                  >
                    {notification.confirmText}
                  </button>
                </>
              ) : (
                <button
                  onClick={closeNotice}
                  className="w-full py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold"
                >
                  OK
                </button>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default Books;